import ApiService from './ApiService';
import { mapObjective } from './ObjectiveFeedService';
import type { RawObjective } from './ObjectiveFeedService';
import type { Objective, ObjectiveProgressDetail } from '@customTypes/objectiveFeed';

export interface SkillInsight {
  skill_name: string;
  strategy_summary: string;
}

export interface ObjectiveSuggestion {
  title: string;
  description: string;
  progress_detail?: ObjectiveProgressDetail;
  discovery_questions?: string[];
  kickoff_body?: string;
  skill_insights?: SkillInsight[];
  // Optional short pill shown on the picker card (e.g. "Start here") to
  // signal a low-pressure default for users unsure which objective to pick.
  badge?: string;
}

class ObjectiveService {
  async getObjectives(): Promise<Objective[]> {
    const { success, data } = await ApiService.get<RawObjective[]>('/objectives');
    if (success && data) {
      return data.map(mapObjective);
    }
    return [];
  }

  async createObjective(payload: { title: string; description?: string; progress_detail?: ObjectiveProgressDetail; skip_kickoff?: boolean; discovery_questions?: string[]; kickoff_body?: string; skill_insights?: { skill_name: string; strategy_summary: string }[] }): Promise<Objective | null> {
    const { success, data } = await ApiService.post<typeof payload, RawObjective>('/objectives', payload);
    if (success && data) {
      return mapObjective(data);
    }
    return null;
  }

  async updateObjective(id: string, payload: { title?: string; description?: string }): Promise<Objective | null> {
    const { success, data } = await ApiService.put<typeof payload, RawObjective>(`/objectives/${id}`, payload);
    if (success && data) {
      return mapObjective(data);
    }
    return null;
  }

  async deleteObjective(id: string): Promise<void> {
    const { success } = await ApiService.delete(`/objectives/${id}`);
    if (!success) {
      throw new Error('Failed to delete objective');
    }
  }

  async getSuggestions(count?: number): Promise<ObjectiveSuggestion[]> {
    const params = count != null ? `?count=${count}` : '';
    const { success, data } = await ApiService.get<{
      suggestions: ObjectiveSuggestion[];
    }>(`/objectives/suggestions${params}`);
    if (success && data?.suggestions) {
      return data.suggestions;
    }
    return [];
  }

  async refreshProgress(): Promise<{ taskId: string; status: string } | null> {
    const { success, data } = await ApiService.post<
      Record<string, never>,
      { task_id: string; status: string }
    >('/objectives/refresh-progress', {});
    if (success && data) {
      return { taskId: data.task_id, status: data.status };
    }
    return null;
  }

  async triggerHeartbeat(): Promise<{ taskId: string; status: string } | null> {
    const { success, data } = await ApiService.post<
      Record<string, never>,
      { task_id: string; status: string }
    >('/objectives/heartbeat', {});
    if (success && data) {
      return { taskId: data.task_id, status: data.status };
    }
    return null;
  }

  async triggerMonitorSweep(): Promise<{ status: string } | null> {
    const { success, data } = await ApiService.post<
      Record<string, never>,
      { mode: string; status: string }
    >('/objectives/monitor-sweep', {});
    if (success && data) {
      return { status: data.status };
    }
    return null;
  }

  /**
   * Internal-only: fire one client-facing daily-plan run for the current
   * company now, bypassing the 8am-local fleet gate. Posts the day's plan to
   * the company's Slack channel (when one is connected — see slackChannelResolved).
   */
  async triggerDailyPlan(): Promise<{ taskId: string; status: string; slackChannelResolved: boolean } | null> {
    const { success, data } = await ApiService.post<
      Record<string, never>,
      { task_id: string; status: string; slack_channel_resolved: boolean }
    >('/objectives/daily-plan', {});
    if (success && data) {
      return { taskId: data.task_id, status: data.status, slackChannelResolved: data.slack_channel_resolved };
    }
    return null;
  }

  /**
   * Internal-only: fire the weekly strategic-review synthesis for the current
   * company now, instead of waiting for the Sunday beat. Arms the same
   * account_review_synthesis run against current state — the per-account Choose
   * fan-out lands in ~a minute.
   */
  async triggerStrategicReview(): Promise<{ status: string; scheduleId?: string; message: string } | null> {
    const { success, data } = await ApiService.post<
      Record<string, never>,
      { status: string; schedule_id?: string; message: string }
    >('/objectives/strategic-review', {});
    if (success && data) {
      return { status: data.status, scheduleId: data.schedule_id, message: data.message };
    }
    return null;
  }

  /**
   * Internal-only: fire the whole MONDAY CHECK-IN now (the grouped audit sweep →
   * strategic review → per-platform Choose cards), any day of the week. Runs in
   * Code Mode + Bedrock Opus. Fire-and-forget: the run lands on the feed shortly.
   */
  async triggerAuditSweep(): Promise<{ status: string; message: string } | null> {
    const { success, data } = await ApiService.post<
      Record<string, never>,
      { status: string; message: string }
    >('/objectives/audit-sweep', {});
    if (success && data) {
      return { status: data.status, message: data.message };
    }
    return null;
  }

  /**
   * Internal-only: fire ONE synthesis audit now (default the Tracking audit). Tracking runs in
   * Code Mode when the company is opted in — the code-mode `reporting.tracking_audit` recipe.
   * Fire-and-forget: the run lands on the feed shortly.
   */
  async triggerSynthesisAudit(
    audit: string = 'Tracking',
  ): Promise<{ status: string; message: string } | null> {
    const { success, data } = await ApiService.post<
      { audit: string },
      { status: string; message: string }
    >('/objectives/synthesis-audit', { audit });
    if (success && data) {
      return { status: data.status, message: data.message };
    }
    return null;
  }

  /**
   * Internal-only: fire the FRIDAY WRAP now (the light read-only weekly close),
   * any day of the week. Fire-and-forget: the wrap report + card land on the feed shortly.
   */
  async triggerFridayWrap(): Promise<{ status: string; message: string } | null> {
    const { success, data } = await ApiService.post<
      Record<string, never>,
      { status: string; message: string }
    >('/objectives/friday-wrap', {});
    if (success && data) {
      return { status: data.status, message: data.message };
    }
    return null;
  }

  /**
   * Internal-only: fire the weekly CREATIVE RUN now, any day of the week. Leo drafts
   * creatives and parks them into Needs Attention shortly for the client to workshop.
   */
  async triggerCreativeRun(): Promise<{ status: string; message: string } | null> {
    const { success, data } = await ApiService.post<
      Record<string, never>,
      { status: string; message: string }
    >('/objectives/creative-run', {});
    if (success && data) {
      return { status: data.status, message: data.message };
    }
    return null;
  }

  /**
   * Internal-only: fire the WEEKLY CREATIVE REFRESH now. One session walks the whole
   * loop — the Brand & Journey map, what actually ran and earned, what competitors
   * moved, twelve variants, and one client card carrying it over.
   */
  async triggerCreativeRefresh(): Promise<{ status: string; message: string } | null> {
    const { success, data } = await ApiService.post<
      Record<string, never>,
      { status: string; message: string }
    >('/objectives/creative-refresh', {});
    if (success && data) {
      return { status: data.status, message: data.message };
    }
    return null;
  }

  /**
   * Internal-only: wipe every trace of the current company's creative-run state so
   * "Run Creative Run" can be re-fired for testing. Clears the dedup guard,
   * workshop card, staged assets in S3+DynamoDB, and conversation memory.
   */
  async resetCreativeRun(): Promise<{ status: string; message: string } | null> {
    const { success, data } = await ApiService.post<
      Record<string, never>,
      { status: string; message: string }
    >('/objectives/creative-run/reset', {});
    if (success && data) {
      return { status: data.status, message: data.message };
    }
    return null;
  }

  async triggerWeeklyLearnings(): Promise<{
    taskIds: string[];
    status: string;
    existingExperiences: number;
    attempting: number;
  } | null> {
    const { success, data } = await ApiService.post<
      Record<string, never>,
      {
        task_ids?: string[];
        status: string;
        existing_experiences: number;
        attempting: number;
      }
    >('/objectives/weekly-learnings', {});
    if (success && data) {
      return {
        taskIds: data.task_ids ?? [],
        status: data.status,
        existingExperiences: data.existing_experiences ?? 0,
        attempting: data.attempting ?? 0,
      };
    }
    return null;
  }

  async getScanInsights(text: string, source: 'summary' | 'brand'): Promise<string[]> {
    const { success, data } = await ApiService.post<
      { text: string; source: string },
      { insights: string[] }
    >('/objectives/scan-insights', { text, source });
    if (success && data?.insights) {
      return data.insights;
    }
    return [];
  }

  async classifyPosts(): Promise<number> {
    const { success, data } = await ApiService.post<
      Record<string, never>,
      { classified: number }
    >('/objectives/classify-posts', {});
    if (success && data) {
      return data.classified;
    }
    return 0;
  }
}

export default new ObjectiveService();
